import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BookOpen, User, ChevronDown, ChevronUp, AlertCircle, CheckCircle, Clock, BarChart3, ExternalLink, Search, GraduationCap, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';

const FormationDistance = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [stagiaires, setStagiaires] = useState([]);
  const [expandedStagiaire, setExpandedStagiaire] = useState(null);
  const [stagiaireData, setStagiaireData] = useState({});
  const [loadingDetail, setLoadingDetail] = useState(null);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const res = await axios.get('/auth/me');
      setUser(res.data);
      const role = res.data.role;

      if (role === 'centre' || role === 'responsable_pedagogique') {
        await loadOverview();
      } else if (role === 'stagiaire') {
        await loadStagiaireDetail(res.data.user_id);
      } else if (role === 'tuteur') {
        await loadTuteurStagiaires(res.data.user_id);
      } else if (role === 'entreprise') {
        await loadEntrepriseStagiaires(res.data.user_id);
      }
    } catch (e) {
    }
    setLoading(false);
  };

  const loadOverview = async () => {
    try {
      const res = await axios.get('/moodle/overview');
      setStagiaires(res.data.stagiaires || []);
    } catch (e) {
    }
  };

  const loadTuteurStagiaires = async (tuteurId) => {
    try {
      const res = await axios.get('/stagiaires');
      const myStagiaires = (res.data || []).filter(s => s.tuteur_id === tuteurId);
      const items = myStagiaires.map(s => ({
        user_id: s.user_id,
        nom: s.user_info?.nom || '',
        prenom: s.user_info?.prenom || '',
        email: s.user_info?.email || '',
        moodle_found: null,
        courses_count: 0,
      }));
      setStagiaires(items);
    } catch (e) {}
  };

  const loadEntrepriseStagiaires = async (entrepriseId) => {
    try {
      const res = await axios.get('/stagiaires');
      const myStagiaires = (res.data || []).filter(s =>
        s.entreprise_id === entrepriseId || s.entreprise_id_2 === entrepriseId
      );
      const items = myStagiaires.map(s => ({
        user_id: s.user_id,
        nom: s.user_info?.nom || '',
        prenom: s.user_info?.prenom || '',
        email: s.user_info?.email || '',
        moodle_found: null,
        courses_count: 0,
      }));
      setStagiaires(items);
    } catch (e) {}
  };

  const loadStagiaireDetail = async (userId) => {
    if (stagiaireData[userId]) return;
    setLoadingDetail(userId);
    try {
      const res = await axios.get(`/moodle/stagiaire/${userId}`);
      setStagiaireData(prev => ({ ...prev, [userId]: res.data }));
    } catch (e) {
      setStagiaireData(prev => ({ ...prev, [userId]: { found: false, courses: [], error: true } }));
    }
    setLoadingDetail(null);
  };

  const handleExpandStagiaire = async (userId) => {
    if (expandedStagiaire === userId) {
      setExpandedStagiaire(null);
    } else {
      setExpandedStagiaire(userId);
      setExpandedCourse(null);
      await loadStagiaireDetail(userId);
    }
  };

  const filteredStagiaires = stagiaires.filter(s => {
    const term = searchTerm.toLowerCase();
    return !term ||
      s.nom?.toLowerCase().includes(term) ||
      s.prenom?.toLowerCase().includes(term) ||
      s.email?.toLowerCase().includes(term);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#8DC63F]" />
      </div>
    );
  }

  // Vue stagiaire : affiche directement ses propres données
  if (user?.role === 'stagiaire') {
    const data = stagiaireData[user.user_id];
    return (
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="flex items-center gap-3">
          <GraduationCap className="h-8 w-8 text-[#8DC63F]" />
          <h1 className="text-2xl font-bold text-[#0F172A]">Formation à distance</h1>
        </div>

        {!data ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-[#8DC63F] mr-2" />
            Chargement...
          </div>
        ) : !data.found ? (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-6 flex items-center gap-3">
              <AlertCircle className="h-6 w-6 text-orange-500" />
              <p className="text-orange-800">Aucun compte Moodle trouvé pour votre email ({data.email}). Contactez votre centre de formation.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <SummaryCard data={data} />
            <CoursesList
              courses={data.courses}
              expandedCourse={expandedCourse}
              setExpandedCourse={setExpandedCourse}
            />
          </>
        )}
      </div>
    );
  }

  // Vue centre / tuteur / entreprise
  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GraduationCap className="h-8 w-8 text-[#8DC63F]" />
          <h1 className="text-2xl font-bold text-[#0F172A]">Formation à distance</h1>
        </div>
        <a
          href="https://fisl.askott.fr"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
          data-testid="moodle-link"
        >
          <ExternalLink className="h-4 w-4" />
          Ouvrir Moodle
        </a>
      </div>

      {/* Barre de recherche */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher un stagiaire..."
          className="pl-10"
          data-testid="search-stagiaire-moodle"
        />
      </div>

      {/* Liste des stagiaires */}
      <div className="space-y-3">
        {filteredStagiaires.length === 0 ? (
          <p className="text-slate-500 text-center py-8">Aucun stagiaire trouvé</p>
        ) : (
          filteredStagiaires.map(stag => {
            const isExpanded = expandedStagiaire === stag.user_id;
            const data = stagiaireData[stag.user_id];
            const isLoading = loadingDetail === stag.user_id;

            return (
              <Card key={stag.user_id} className={`transition-all ${isExpanded ? 'border-[#8DC63F] shadow-md' : 'hover:border-slate-300'}`}>
                <div
                  className="p-4 flex items-center justify-between cursor-pointer"
                  onClick={() => handleExpandStagiaire(stag.user_id)}
                  data-testid={`stagiaire-row-${stag.user_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#8DC63F]/10 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-[#8DC63F]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0F172A]">{stag.prenom} {stag.nom}</p>
                      <p className="text-sm text-slate-500">{stag.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {stag.moodle_found === false && (
                      <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded-full">Pas de compte Moodle</span>
                    )}
                    {stag.courses_count > 0 && (
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">{stag.courses_count} cours</span>
                    )}
                    {isLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                    ) : (
                      isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t px-4 pb-4">
                    {isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-5 w-5 animate-spin text-[#8DC63F] mr-2" />
                        Chargement des données Moodle...
                      </div>
                    ) : data && !data.found ? (
                      <div className="py-4 flex items-center gap-2 text-orange-700">
                        <AlertCircle className="h-5 w-5" />
                        Aucun compte Moodle trouvé pour {data.email}
                      </div>
                    ) : data ? (
                      <div className="pt-4 space-y-4">
                        <SummaryCard data={data} />
                        <CoursesList
                          courses={data.courses}
                          expandedCourse={expandedCourse}
                          setExpandedCourse={setExpandedCourse}
                        />
                      </div>
                    ) : null}
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

// Carte résumé
const SummaryCard = ({ data }) => {
  const enrolled = data.summary?.total_courses_enrolled || 0;
  const avgCompletion = data.summary?.avg_completion || 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div className="bg-blue-50 rounded-lg p-4 flex items-center gap-3">
        <BookOpen className="h-8 w-8 text-blue-500" />
        <div>
          <p className="text-2xl font-bold text-blue-700">{enrolled}</p>
          <p className="text-xs text-blue-600">Cours inscrits</p>
        </div>
      </div>
      <div className="bg-green-50 rounded-lg p-4 flex items-center gap-3">
        <BarChart3 className="h-8 w-8 text-green-500" />
        <div>
          <p className="text-2xl font-bold text-green-700">{avgCompletion}%</p>
          <p className="text-xs text-green-600">Avancement moyen</p>
        </div>
      </div>
      <div className="bg-purple-50 rounded-lg p-4 flex items-center gap-3">
        <GraduationCap className="h-8 w-8 text-purple-500" />
        <div>
          <p className="text-2xl font-bold text-purple-700">{data.moodle_fullname || '—'}</p>
          <p className="text-xs text-purple-600">Compte Moodle</p>
        </div>
      </div>
    </div>
  );
};

// Liste des cours
const CoursesList = ({ courses, expandedCourse, setExpandedCourse }) => {
  if (!courses || courses.length === 0) {
    return <p className="text-slate-500 text-sm">Aucun cours BP Motonautisme inscrit.</p>;
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
        <BookOpen className="h-4 w-4" />
        Cours BP Motonautisme ({courses.length})
      </h3>
      {courses.map(course => {
        const isOpen = expandedCourse === course.id;
        const completion = course.completion;
        const completionPct = completion?.percentage || 0;

        return (
          <div key={course.id} className="border rounded-lg overflow-hidden">
            <div
              className="p-3 flex items-center justify-between cursor-pointer hover:bg-slate-50"
              onClick={() => setExpandedCourse(isOpen ? null : course.id)}
              data-testid={`course-row-${course.id}`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <BookOpen className="h-4 w-4 text-blue-500 flex-shrink-0" />
                <span className="text-sm font-medium text-[#0F172A] truncate">{course.fullname}</span>
                {course.lastaccess > 0 && (
                  <span className="text-xs text-slate-400 flex-shrink-0" title="Dernier accès">
                    {new Date(course.lastaccess * 1000).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                {completion && (
                  <div className="flex items-center gap-2 w-40">
                    <Progress value={completionPct} className="h-2 flex-1" />
                    <span className="text-xs text-slate-600 w-10 text-right">{completionPct}%</span>
                  </div>
                )}
                {course.total_grade && course.total_grade !== '—' ? (
                  <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">
                    {course.total_percentage || course.total_grade}
                  </span>
                ) : (
                  <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded">—</span>
                )}
                {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
              </div>
            </div>

            {isOpen && (
              <div className="border-t bg-slate-50 p-3">
                {/* Dernier accès */}
                {course.lastaccess > 0 && (
                  <div className="mb-3 flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-blue-500" />
                    <span className="text-slate-600">
                      Dernier accès : {new Date(course.lastaccess * 1000).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                )}
                {course.lastaccess === 0 && (
                  <div className="mb-3 flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-400">Jamais accédé</span>
                  </div>
                )}

                {/* Complétion */}
                {completion && (
                  <div className="mb-3 flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-slate-500" />
                    <span className="text-slate-600">
                      {completion.completed}/{completion.total} activités complétées
                    </span>
                  </div>
                )}

                {/* Notes détaillées */}
                {course.grades && course.grades.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-slate-500 mb-2">Détail des notes :</p>
                    {course.grades.map((g, i) => (
                      <div key={i} className="flex items-center justify-between py-1 px-2 rounded text-sm even:bg-white">
                        <span className="text-slate-700">{g.name}</span>
                        <div className="flex items-center gap-2">
                          {g.grade && g.grade !== '—' ? (
                            <>
                              <span className="font-medium text-[#0F172A]">{g.grade}</span>
                              {g.percentage && <span className="text-xs text-slate-500">{g.percentage}</span>}
                            </>
                          ) : (
                            <span className="text-slate-400 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              Non évalué
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">Aucune note disponible</p>
                )}

                {/* Total */}
                {course.total_grade && course.total_grade !== '—' && (
                  <div className="mt-3 pt-2 border-t flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#0F172A]">Total du cours</span>
                    <span className="text-sm font-bold text-green-700">{course.total_grade} {course.total_percentage && `(${course.total_percentage})`}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FormationDistance;
